import type { ComercialOverview, Health, Prioridad } from '@ops/contracts'
import { CHANNEL_STATUS_LABELS } from '@ops/contracts'

import { plural } from '../../lib/format'
import { DataTable, Panel, StatusPill, type Column } from '../../ui'
import { Vacio } from '../comunes'
import { canalesSinLectura } from './narrativa'

/**
 * Canales de adquisición del Overview comercial.
 *
 * El payload de Overview trae solo el estado, la escalabilidad y la prioridad:
 * la conversión y el costo viven en el módulo de Adquisición, y el costo además
 * es plata, así que acá no aparece ni como columna vacía.
 *
 * Un canal que no está activo es un frente: **no hay lectura de conversión**. Se
 * dice con esas palabras en la fila, no con un color de fondo.
 */
type Canal = ComercialOverview['canales'][number]

/** Estado del canal → severidad. El texto del estado viaja igual en la pill. */
export function severidadDeCanal(status: string): Health {
  if (status === CHANNEL_STATUS_LABELS.activo) return 'ok'
  if (status === CHANNEL_STATUS_LABELS.en_prueba) return 'warning'
  if (status === CHANNEL_STATUS_LABELS.pausado) return 'critical'
  return 'unknown'
}

const SEVERIDAD_ESCALABILIDAD: Record<Prioridad, Health> = {
  alta: 'ok',
  media: 'warning',
  baja: 'critical',
}

export interface CanalesPanelProps {
  canales: ComercialOverview['canales']
}

export function CanalesPanel({ canales }: CanalesPanelProps) {
  const sinLectura = canalesSinLectura(canales)

  if (canales.length === 0) {
    return (
      <Panel titulo="Los canales y su escalabilidad" padding="sm">
        <Vacio icono={null}
          mensaje="Todavía no hay canales cargados"
          detalle="Los canales se dan de alta en Adquisición, con su costo y su conversión."
          punteada
        />
      </Panel>
    )
  }

  const columnas: ReadonlyArray<Column<Canal>> = [
    {
      key: 'nombre',
      header: 'Canal',
      rowHeader: true,
      width: '180px',
      sortValue: (c) => c.nombre,
      render: (c) => c.nombre,
    },
    {
      key: 'status',
      header: 'Estado',
      width: '140px',
      sortValue: (c) => c.status,
      render: (c) => (
        <StatusPill severidad={severidadDeCanal(c.status)} size="sm">
          {c.status}
        </StatusPill>
      ),
    },
    {
      key: 'escalabilidad',
      header: 'Escalabilidad',
      width: '140px',
      sortValue: (c) => c.escalabilidad,
      render: (c) => (
        <StatusPill
          severidad={SEVERIDAD_ESCALABILIDAD[c.escalabilidad]}
          size="sm"
          icono={false}
        >
          {c.escalabilidad}
        </StatusPill>
      ),
    },
    {
      key: 'prioridad',
      header: 'Prioridad',
      width: '110px',
      sortValue: (c) => c.prioridad,
      render: (c) => c.prioridad,
    },
    {
      key: 'lectura',
      header: 'Lectura de conversión',
      render: (c) =>
        severidadDeCanal(c.status) === 'ok' ? (
          <span className="text-micro text-muted-strong">
            Se mide en Adquisición
          </span>
        ) : (
          <span className="text-micro text-warning-text">
            Sin lectura: el canal no está activo
          </span>
        ),
    },
  ]

  return (
    <Panel
      titulo="Los canales y su escalabilidad"
      subtitulo={
        sinLectura.length === 0
          ? 'Todos los canales están activos'
          : `${sinLectura.length} de ${canales.length} ${plural(sinLectura.length, 'canal', 'canales')} sin lectura de conversión`
      }
      padding="none"
      footer="La conversión y el costo por canal viven en Adquisición."
    >
      <DataTable
        columns={columnas}
        rows={canales}
        rowKey={(c) => c.nombre}
        rowSeverity={(c) => severidadDeCanal(c.status)}
        densidadPorSeveridad
        caption="Canales del período, con su estado tal como lo declara Adquisición"
      />
    </Panel>
  )
}
